import store from "./store";
import types from "./actions/types";

// Basic phone number validation patterns
const phonePatterns = {
  // US/Canada: +1XXXXXXXXXX or 1XXXXXXXXXX or XXXXXXXXXX (10 digits)
  us: /^(\+?1)?[2-9]\d{2}[2-9]\d{2}\d{4}$/,
  // International: +XX followed by 7-15 digits
  international: /^\+[1-9]\d{1,14}$/,
  // General pattern for most phone numbers (7-15 digits with optional + and country code)
  general: /^(\+\d{1,3})?[\s\-\.]?\(?[\d\s\-\.\(\)]{7,15}$/
};

/**
 * Clean phone number by removing non-digit characters except +
 */
function cleanPhoneNumber(phone) {
  if (!phone) return "";
  // Keep only digits and + sign
  return phone.replace(/[^\d+]/g, "");
}

/**
 * Format phone number for display (US format)
 */
function formatPhoneNumber(phone) {
  const cleaned = cleanPhoneNumber(phone);
  
  // Remove leading +1 or 1 for US numbers
  let digits = cleaned.replace(/^\+?1/, "");
  
  // If we have exactly 10 digits, format as (XXX) XXX-XXXX
  if (digits.length === 10) {
    return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
  }
  
  // For international numbers, just return the cleaned version with +
  if (cleaned.startsWith("+")) {
    return cleaned;
  }
  
  // If no + and not 10 digits, add +1 prefix for US
  if (digits.length === 10) {
    return `+1${digits}`;
  }
  
  return cleaned;
}

/**
 * Format phone number with proper country code
 */
function formatPhoneWithCountryCode(phone) {
  const cleaned = cleanPhoneNumber(phone);
  
  // If already has country code, return as is
  if (cleaned.startsWith("+")) {
    return cleaned;
  }
  
  // For 10-digit numbers (US/Canada), add +1
  if (cleaned.length === 10) {
    return `+1${cleaned}`;
  }
  
  // For 11-digit numbers starting with 1 (US/Canada), add +
  if (cleaned.length === 11 && cleaned.startsWith("1")) {
    return `+${cleaned}`;
  }
  
  // For other numbers, just add +
  return `+${cleaned}`;
}

/**
 * Validate phone number using basic patterns
 */
function validatePhoneNumber(phone) {
  if (!phone) return false;
  
  const cleaned = cleanPhoneNumber(phone);
  
  // Check various patterns
  return (
    phonePatterns.us.test(cleaned) ||
    phonePatterns.international.test(cleaned) ||
    phonePatterns.general.test(cleaned)
  );
}

/**
 * Basic phone lookup replacement - performs validation and formatting
 * Returns an object similar to Twilio's response format for compatibility
 */
export async function lookup(phone) {
  try {
    store.dispatch({ type: types.PROGRESS_START, modal: true });
    
    // Simulate a small delay to match Twilio behavior
    await new Promise(resolve => setTimeout(resolve, 500));
    
    const isValid = validatePhoneNumber(phone);
    
    store.dispatch({ type: types.PROGRESS_END });
    
    if (!isValid) {
      return null; // Invalid phone number
    }
    
    // Return object in Twilio-like format for compatibility
    return {
      phone_number: formatPhoneWithCountryCode(phone),
      national_format: formatPhoneNumber(phone)
    };
  } catch (err) {
    store.dispatch({ type: types.PROGRESS_END });
    return null;
  }
}

/**
 * Enhanced phone validation with more detailed results
 */
export function validatePhone(phone) {
  if (!phone) {
    return {
      isValid: false,
      formatted: "",
      cleaned: "",
      error: "Phone number is required"
    };
  }
  
  const cleaned = cleanPhoneNumber(phone);
  const isValid = validatePhoneNumber(phone);
  
  return {
    isValid,
    formatted: isValid ? formatPhoneNumber(phone) : "",
    cleaned: cleaned,
    error: isValid ? "" : "Invalid phone number format"
  };
}